import { useTranslations } from 'next-intl';
import { API_URL, COPY_RIGHT, SHENGSHU_WEBSITE_URL, SITE } from '@/app/constants';
import { useLocale } from '@/i18n/routing';
import { CN_SOCIAL_LINKS, EN_SOCIAL_LINKS, SUPPORT_HELP_LINK, SUPPORT_TUTORIAL_LINK } from './constants';
import type { FooterConfigItem, RegistrationConfig } from './types';

export const getSocialLinks = () => {
  return SITE === 'china' ? CN_SOCIAL_LINKS : EN_SOCIAL_LINKS;
};

export const getGlobalENFooterConfig = (trans: ReturnType<typeof useTranslations>): FooterConfigItem[] => [
  {
    title: trans('function.title'),
    id: 'globalENFunction',
    children: [
      {
        id: 'function1',
        href: '/ai-reference-to-video',
        text: trans('function.reference'),
      },
      {
        id: 'function2',
        href: '/ai-image-to-video',
        text: trans('function.image'),
      },
      {
        id: 'function3',
        href: '/ai-text-to-video',
        text: trans('function.text'),
      },
      {
        id: 'function4',
        href: '/ai-sound-effect-generator',
        text: trans('function.sound'),
      },
      {
        id: 'function5',
        href: '/ai-templates',
        text: trans('function.templates'),
      },
      {
        id: 'function6',
        href: '/ai-image-generator',
        text: trans('function.image-generator'),
      },
      {
        id: 'function7',
        href: '/vidu-q3',
        text: trans('function.vidu-q3'),
      },
      {
        id: 'function8',
        href: '/tools',
        text: trans('function.all-tools'),
      },
    ],
  },
  {
    title: trans('tools.title'),
    id: 'globalENTools',
    children: [
      {
        id: 'tools1',
        href: '/ai-video-generator',
        text: trans('tools.ai-video-generator'),
        rel: '',
        target: '_blank',
      },
      {
        id: 'tools2',
        href: '/ai-animation-generator',
        text: trans('tools.ai-animation-generator'),
        rel: '',
        target: '_blank',
      },
      {
        id: 'tools3',
        href: '/ai-image-animator',
        text: trans('tools.ai-image-animator'),
        rel: '',
        target: '_blank',
      },
      {
        id: 'tools4',
        href: '/ai-ad-generator',
        text: trans('tools.ai-ad-generator'),
        rel: '',
        target: '_blank',
      },
      {
        id: 'tools5',
        href: '/ai-hug-generator',
        text: trans('tools.ai-hug-generator'),
        rel: '',
        target: '_blank',
      },
      {
        id: 'tools6',
        href: '/ai-kissing-generator',
        text: trans('tools.ai-kissing-generator'),
        rel: '',
        target: '_blank',
      },
      {
        id: 'tools7',
        href: '/ai-video-tools',
        text: trans('tools.viewAll'),
        rel: '',
        target: '_blank',
      },
    ],
  },
  {
    title: trans('enterprise.title'),
    id: 'globalENEnterprise',
    children: [
      {
        id: 'enterprise1',
        href: API_URL,
        text: trans('enterprise.api'),
        target: '_blank',
      },
    ],
  },
  {
    title: trans('company.title'),
    id: 'globalENCompany',
    children: [
      {
        id: 'company1',
        href: 'mailto:support@vidu.com',
        text: trans('company.contact'),
        target: '_blank',
        rel: 'noreferrer nofollow',
      },
      {
        id: 'company2',
        href: '/cpp',
        text: trans('company.creative'),
        target: '_blank',
        rel: 'noreferrer nofollow',
      },
    ],
  },
];

export const getGlobalZHFooterConfig = (trans: ReturnType<typeof useTranslations>): FooterConfigItem[] => [
  {
    title: trans('function.title'),
    id: 'globalZHfunction',
    children: [
      {
        id: 'function1',
        href: '/ai-reference-to-video',
        text: trans('function.reference'),
      },
      {
        id: 'function2',
        href: '/ai-image-to-video',
        text: trans('function.image'),
      },
      {
        id: 'function3',
        href: '/ai-text-to-video',
        text: trans('function.text'),
      },
      {
        id: 'function4',
        href: '/ai-sound-effect-generator',
        text: trans('function.sound'),
      },
      {
        id: 'function5',
        href: '/ai-image-generator',
        text: trans('function.image-generator'),
      },
      {
        id: 'function6',
        href: '/vidu-q3',
        text: trans('function.vidu-q3'),
      },
    ],
  },
  {
    title: trans('enterprise.title'),
    id: 'globalZHEnterprise',
    children: [
      {
        id: 'enterprise1',
        href: API_URL,
        text: trans('enterprise.api'),
        target: '_blank',
      },
    ],
  },
  {
    title: trans('support.title'),
    id: 'globalZHSupport',
    children: [
      {
        id: 'support1',
        href: SUPPORT_HELP_LINK,
        text: trans('support.help'),
        target: '_blank',
        reportElement: {
          eventName: 'element_click',
          key: 'support_help',
          params: {
            element_name: 'Help Center',
          },
        },
        rel: 'noreferrer nofollow',
      },
      {
        id: 'support2',
        href: SUPPORT_TUTORIAL_LINK,
        text: trans('support.tutorial'),
        target: '_blank',
        reportElement: {
          eventName: 'element_click',
          key: 'support_tutorial',
          params: {
            element_name: 'Tutorial',
          },
        },
        rel: 'noreferrer nofollow',
      },
    ],
  },
  {
    title: trans('company.title'),
    id: 'globalZHCompany',
    children: [
      {
        id: 'company1',
        href: SHENGSHU_WEBSITE_URL.zh,
        text: trans('company.about'),
        target: '_blank',
        rel: 'noreferrer nofollow',
      },
      {
        id: 'company2',
        href: 'mailto:support@vidu.com',
        text: trans('company.contact'),
        target: '_blank',
        rel: 'noreferrer nofollow',
      },
      {
        id: 'company3',
        href: '/cpp',
        text: trans('company.creative'),
        target: '_blank',
        rel: 'noreferrer nofollow',
      },
    ],
  },
];

export const getChinaFooterConfig = (trans: ReturnType<typeof useTranslations>): FooterConfigItem[] => [
  {
    id: 'chinaFunction',
    title: trans('function.title'),
    children: [
      {
        id: 'function1',
        href: '/create/character2video',
        text: trans('function.reference'),
      },
      {
        id: 'function2',
        href: '/create/img2video',
        text: trans('function.image'),
      },
      {
        id: 'function3',
        href: '/create/text2video',
        text: trans('function.text'),
      },
    ],
  },
  {
    id: 'chinaEnterprise',
    title: trans('enterprise.title'),
    children: [
      {
        id: 'enterprise1',
        href: API_URL,
        text: trans('enterprise.api'),
        target: '_blank',
      },
    ],
  },
  {
    id: 'chinaSupport',
    title: trans('support.title'),
    children: [
      {
        id: 'support1',
        href: SUPPORT_HELP_LINK,
        text: trans('support.help'),
        target: '_blank',
        reportElement: {
          eventName: 'element_click',
          key: 'support_help',
          params: {
            element_name: 'Help Center',
          },
        },
        rel: 'noreferrer nofollow',
      },
      {
        id: 'support2',
        href: SUPPORT_TUTORIAL_LINK,
        text: trans('support.tutorial'),
        target: '_blank',
        reportElement: {
          eventName: 'element_click',
          key: 'support_tutorial',
          params: {
            element_name: 'Tutorial',
          },
        },
        rel: 'noreferrer nofollow',
      },
    ],
  },
  {
    title: trans('company.title'),
    id: 'chinaCompany',
    children: [
      {
        id: 'company1',
        href: SHENGSHU_WEBSITE_URL.zh,
        text: trans('company.about'),
        target: '_blank',
        rel: 'noreferrer nofollow',
      },
      {
        id: 'company2',
        href: 'mailto:support@vidu.cn',
        text: '联系我们：support@vidu.cn',
        target: '_blank',
        rel: 'noreferrer nofollow',
      },
    ],
  },
];

export const getFooterConfig = (): FooterConfigItem[] => {
  const locale = useLocale();
  const trans = useTranslations('home3.footer');

  return SITE === 'global'
    ? locale === 'zh'
      ? getGlobalZHFooterConfig(trans)
      : locale === 'en'
        ? getGlobalENFooterConfig(trans)
        : getGlobalENFooterConfig(trans).filter((item) => item.id !== 'globalENTools') // 其他语言暂时不显示AI Tools
    : getChinaFooterConfig(trans);
};

export const getTermsAndPrivacy = (): RegistrationConfig[] => {
  const trans = useTranslations('home3.footer');

  const chinaOnlyLinks: RegistrationConfig[] =
    SITE === 'china'
      ? [
          {
            id: 'termsAndPrivacyPrivacySummary',
            href: '/privacy-summary',
            text: trans('privacySummary'),
            target: '_blank',
            rel: 'noreferrer nofollow',
            icon: '',
            alt: '',
          },
          {
            id: 'termsAndPrivacyInfoCollection',
            href: '/personal-info-collection',
            text: trans('personalInfoCollection'),
            target: '_blank',
            rel: 'noreferrer nofollow',
            icon: '',
            alt: '',
          },
          {
            id: 'termsAndPrivacyInfoSharing',
            href: '/personal-info-sharing',
            text: trans('personalInfoSharing'),
            target: '_blank',
            rel: 'noreferrer nofollow',
            icon: '',
            alt: '',
          },
        ]
      : [];

  return [
    {
      id: 'termsAndPrivacy1',
      href: '',
      text: COPY_RIGHT,
      target: '',
      rel: '',
      icon: '',
      alt: '',
    },
    {
      id: 'termsAndPrivacy2',
      href: '/terms',
      text: trans('terms'),
      target: '_blank',
      rel: 'noreferrer nofollow',
      icon: '',
      alt: '',
    },
    {
      id: 'termsAndPrivacy3',
      href: '/privacy',
      text: trans('privacy'),
      target: '_blank',
      rel: 'noreferrer nofollow',
      icon: '',
      alt: '',
    },
    ...(SITE === 'global'
      ? [
          {
            id: 'termsAndPrivacyCookiesPolicy',
            href: '/cookies',
            text: trans('cookiesPolicy'),
            target: '_blank',
            rel: 'noreferrer nofollow',
            icon: '',
            alt: '',
          },
        ]
      : []),
    ...chinaOnlyLinks,
  ];
};

export const getRegistrationConfig = (): RegistrationConfig[] => {
  return [
    {
      id: 'registration1',
      href: 'http://www.beian.gov.cn/',
      text: '京公网安备11010802045162号',
      target: '_blank',
      rel: 'noreferrer nofollow',
      icon: 'https://image01.vidu.zone/vidu/media-asset/111418-aICL2VPbzelC9GNw.png',
      alt: '备案图标',
    },
    {
      id: 'registration2',
      href: '',
      text: 'UniDiffuser多模态大模型',
      target: '',
      rel: '',
      icon: '',
      alt: '',
    },
    {
      id: 'registration3',
      href: '',
      text: 'Beijing-UniDiffuser-20240226',
      target: '',
      rel: '',
      icon: '',
      alt: '',
    },
    {
      id: 'registration4',
      href: 'https://beian.miit.gov.cn/',
      text: '京ICP备2023012431号-9',
      target: '_blank',
      rel: 'noreferrer nofollow',
      icon: '',
      alt: '',
    },
  ];
};
